import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Image as ImageIcon, Video, FileText as DocIcon, Mic, Search, Upload, Settings as SettingsIcon, Folder, Trash2, Pencil, Layers, Tag, Plus, X } from 'lucide-react';
import PageHeader, { AccountSelector, EmptyState } from '../../components/PageHeader';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api, { apiError } from '../../lib/api';
import HoverMedia, { TYPE_ICONS } from './vault/HoverMedia';
import { UploadDialog, AddTagDialog, EditDialog, VaultSettingsDialog, LightboxDialog, SORTS } from './vault/Dialogs';
import { useAuth } from '../../context/AuthContext';

const PAGE_SIZE = 50;

export default function Vault() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm('vault_manage'); // chatters: view & send only, no upload/edit/delete
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState('');
  const [items, setItems] = useState([]);
  const [type, setType] = useState('all');
  const [tag, setTag] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tagging, setTagging] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cacheStats, setCacheStats] = useState(null);
  const [tagColors, setTagColors] = useState({});
  const [colorPicker, setColorPicker] = useState(null); // tag name being edited
  const [preview, setPreview] = useState(null); // vault item to preview in lightbox

  useEffect(() => {
    api.get('/accounts').then(({ data }) => {
      setAccounts(data);
      if (data[0]) setAccount(prev => prev || data[0].id);
    }).catch(e => toast.error(apiError(e, 'Impossible de charger les comptes')));
    api.get('/vault/cache-stats').then(({ data }) => setCacheStats(data)).catch(() => {});
    api.get('/vault/tag-colors').then(({ data }) => setTagColors(data || {})).catch(() => {});
  }, []);

  const setTagColor = async (t, color) => {
    setTagColors(prev => ({ ...prev, [t]: color }));
    setColorPicker(null);
    try { await api.post('/vault/tag-colors', { tag: t, color }); } catch (e) { toast.error(apiError(e, 'Impossible d\'enregistrer la couleur')); }
  };

  const load = useCallback(async (acc) => {
    if (!acc) return;
    try {
      const { data } = await api.get('/vault', { params: acc === 'all' ? {} : { account_id: acc } });
      setItems(data);
      setSelected([]);
      setPage(1);
    } catch (e) { toast.error(apiError(e, 'Impossible de charger le Vault')); }
  }, []);
  useEffect(() => { load(account); }, [account, load]);

  const remove = async (ids) => {
    try {
      await Promise.all(ids.map(id => api.delete(`/vault/${id}`)));
      setItems(items.filter(x => !ids.includes(x.id)));
      setSelected(selected.filter(x => !ids.includes(x)));
      toast.success(ids.length > 1 ? `${ids.length} médias supprimés` : 'Média supprimé');
    } catch (e) { toast.error(apiError(e, 'Échec de la suppression')); }
  };

  const applyTag = async (t, color) => {
    try {
      const updated = await Promise.all(selected.map(id => {
        const item = items.find(x => x.id === id);
        const tags = Array.from(new Set([...(item?.tags || []), t]));
        return api.patch(`/vault/${id}`, { tags }).then(r => r.data);
      }));
      setItems(prev => prev.map(x => updated.find(u => u.id === x.id) || x));
      if (color) setTagColor(t, color);
      toast.success(`Tag « ${t} » ajouté à ${updated.length} médias`);
      setTagging(false);
    } catch (e) { toast.error(apiError(e, 'Impossible d\'ajouter le tag')); }
  };

  // RETIRER un tag des médias sélectionnés (Marlon, 12/09) : ce bouton manquait, et
  // faute de mieux il a supprimé 14 médias alors qu'il voulait seulement leur enlever
  // « lifestyle » (registre #121). t = nom du tag, ou null pour retirer TOUS les tags.
  const removeTag = async (t) => {
    const cibles = selected
      .map(id => items.find(x => x.id === id))
      .filter(item => item && (t === null ? (item.tags || []).length > 0 : (item.tags || []).includes(t)));
    if (!cibles.length) return;
    try {
      const updated = await Promise.all(cibles.map(item => {
        const tags = t === null ? [] : (item.tags || []).filter(x => x !== t);
        return api.patch(`/vault/${item.id}`, { tags })
          .then(r => (r.data && r.data.id ? r.data : { ...item, tags }));
      }));
      setItems(prev => prev.map(x => updated.find(u => u.id === x.id) || x));
      toast.success(t === null
        ? `Tags retirés de ${updated.length} média${updated.length > 1 ? 's' : ''}`
        : `Tag « ${t} » retiré de ${updated.length} média${updated.length > 1 ? 's' : ''}`);
    } catch (e) { toast.error(apiError(e, 'Impossible de retirer le tag')); }
  };

  // Tags présents sur la sélection, avec le nombre de médias sélectionnés qui les portent.
  const selectedTags = useMemo(() => {
    const n = {};
    selected.forEach(id => {
      const item = items.find(x => x.id === id);
      (item?.tags || []).forEach(tg => { n[tg] = (n[tg] || 0) + 1; });
    });
    return Object.entries(n).sort((a, b) => b[1] - a[1]);
  }, [selected, items]);

  const counts = useMemo(() => ({
    all: items.length,
    photo: items.filter(v => v.type === 'photo').length,
    video: items.filter(v => v.type === 'video').length,
    document: items.filter(v => v.type === 'document').length,
    voice: items.filter(v => v.type === 'voice').length,
  }), [items]);
  const allTags = useMemo(() => Array.from(new Set(items.flatMap(v => v.tags))), [items]);
  const filtered = useMemo(() => {
    const f = items.filter(v =>
      (type === 'all' || v.type === type) &&
      (!tag || (tag === '__none__' ? v.tags.length === 0 : v.tags.includes(tag))) &&
      v.name.toLowerCase().includes(q.toLowerCase())
    );
    if (sort === 'name') return [...f].sort((a, b) => a.name.localeCompare(b.name));
    if (sort === 'sent') return [...f].sort((a, b) => b.sent - a.sent);
    return [...f].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [items, type, tag, q, sort]);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const [lastSelectedId, setLastSelectedId] = useState(null);

  const toggleSelect = (id, e) => {
    // Shift+click → select range from last clicked item to this one (against `filtered`)
    if (e && e.shiftKey && lastSelectedId && lastSelectedId !== id) {
      const ids = filtered.map((v) => v.id);
      const a = ids.indexOf(lastSelectedId);
      const b = ids.indexOf(id);
      if (a >= 0 && b >= 0) {
        const [from, to] = a < b ? [a, b] : [b, a];
        const range = ids.slice(from, to + 1);
        setSelected((prev) => Array.from(new Set([...prev, ...range])));
        setLastSelectedId(id);
        return;
      }
    }
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    setLastSelectedId(id);
  };
  const selectAll = () => setSelected(selected.length === filtered.length ? [] : filtered.map(v => v.id));

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="vault-page">
      <PageHeader
        title="Vault"
        subtitle={`${items.length} médias · ${allTags.length} tag${allTags.length > 1 ? 's' : ''}`}
        actions={<AccountSelector value={account} onChange={setAccount} accounts={accounts} allowAll />}
        onRefresh={() => load(account)}
      />

      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="vault-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un média..." className="bg-white/5 border-white/10 text-white pl-9 h-10" />
        </div>
        <select data-testid="vault-sort-select" value={sort} onChange={e => setSort(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 text-xs text-white h-10 outline-none">
          {SORTS.map(([k, l]) => <option key={k} value={k} className="bg-[#0B0B0E]">{`Tri : ${l}`}</option>)}
        </select>
        {canManage && <Button data-testid="vault-settings-button" variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10 h-10" onClick={() => setSettingsOpen(true)}><SettingsIcon size={14} className="mr-1.5" /> Réglages</Button>}
        {canManage && <Button data-testid="vault-add-tag-button" variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10 h-10" onClick={() => setTagging(true)}><Tag size={14} className="mr-1.5" /> Ajouter un tag</Button>}
        {canManage && <Button data-testid="upload-media-button" className="bg-indigo-500 hover:bg-indigo-600 h-10" onClick={() => setUploading(true)}><Upload size={14} className="mr-1.5" /> Importer un média</Button>}
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-3 space-y-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-2 mb-2">Type</div>
            <div className="space-y-0.5">
              {[['all', 'Tous les types', Layers], ['photo', 'Photo', ImageIcon], ['video', 'Vidéo', Video], ['document', 'Document', DocIcon], ['voice', 'Vocal', Mic]].map(([k, l, I]) => (
                <button key={k} data-testid={`vault-type-${k}`} onClick={() => { setType(k); setPage(1); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between ${type === k ? 'bg-indigo-500/15 text-white border border-indigo-500/30' : 'text-slate-400 hover:bg-white/5 border border-transparent'}`}>
                  <span className="flex items-center gap-2"><I size={12} /> {l}</span>
                  <span className="text-[10px] text-slate-500">{counts[k]}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
            <div className="flex items-center justify-between px-2 mb-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Tags</span>
              <span className="text-[10px] text-slate-600">{allTags.length}</span>
            </div>
            <div className="space-y-0.5">
              <button onClick={() => setTag('')} className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between ${!tag ? 'bg-indigo-500/15 text-white border border-indigo-500/30' : 'text-slate-400 hover:bg-white/5 border border-transparent'}`}>
                <span className="flex items-center gap-2"><Layers size={12} /> Tous les tags</span>
                <span className="text-[10px] text-slate-500">{items.length}</span>
              </button>
              {allTags.map(t => (
                <div key={t} className={`flex items-center rounded-lg group/tag ${tag === t ? 'bg-indigo-500/15 border border-indigo-500/30' : 'border border-transparent hover:bg-white/5'}`}>
                  <button onClick={(e) => { e.stopPropagation(); if (canManage) setColorPicker(colorPicker === t ? null : t); }}
                    className="pl-3 pr-1 py-2 shrink-0" title={canManage ? 'Changer la couleur' : ''}>
                    <span className="w-2.5 h-2.5 rounded-full block" style={{ backgroundColor: tagColors[t] || '#64748b' }} />
                  </button>
                  <button data-testid={`vault-tag-${t}`} onClick={() => setTag(tag === t ? '' : t)} className={`flex-1 text-left pr-2 py-2 text-sm flex items-center justify-between ${tag === t ? 'text-white' : 'text-slate-400 hover:text-white'}`}>
                    <span className="truncate">{t}</span>
                    <span className="text-[10px] text-slate-500 ml-2">{items.filter(v => v.tags.includes(t)).length}</span>
                  </button>
                  {canManage && <button
                    data-testid={`rename-vault-tag-${t}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      const fresh = window.prompt(`Renommer le tag « ${t} » en :`, t);
                      if (!fresh || fresh.trim() === '' || fresh.trim() === t) return;
                      try {
                        const { data } = await api.post('/vault/tags/rename', { old: t, new: fresh.trim() });
                        toast.success(`Renommé sur ${data.updated} médias`);
                        load(account);
                      } catch (err) { toast.error(apiError(err, 'Échec du renommage')); }
                    }}
                    className="opacity-0 group-hover/tag:opacity-100 px-1.5 py-1 text-slate-500 hover:text-indigo-300 transition-opacity" title="Renommer le tag"
                  ><Pencil size={10} /></button>}
                  {canManage && <button
                    data-testid={`delete-vault-tag-${t}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm(`Supprimer le tag « ${t} » de TOUS les médias ? C'est irréversible.`)) return;
                      try {
                        const { data } = await api.delete(`/vault/tags/${encodeURIComponent(t)}`);
                        toast.success(`Retiré de ${data.modified} médias`);
                        if (tag === t) setTag('');
                        load(account);
                      } catch (err) { toast.error(apiError(err, 'Échec de la suppression')); }
                    }}
                    className="opacity-0 group-hover/tag:opacity-100 pr-2 py-1 text-slate-500 hover:text-rose-400 transition-opacity" title="Supprimer le tag"
                  ><Trash2 size={10} /></button>}
                  {colorPicker === t && (
                    <div className="absolute z-20 mt-9 ml-2 bg-[#0B0B0E] border border-white/15 rounded-lg p-2 flex flex-wrap gap-1 shadow-xl shadow-black/50" data-testid={`color-picker-${t}`}>
                      {['#6366f1', '#3b82f6', '#f43f5e', '#f59e0b', '#10b981', '#a78bfa', '#ec4899', '#64748b'].map(c => (
                        <button key={c} onClick={() => setTagColor(t, c)} className="w-5 h-5 rounded-full border-2 border-white/10 hover:border-white" style={{ backgroundColor: c }} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {items.some(v => v.tags.length === 0) && (
                <button data-testid="vault-tag-none" onClick={() => setTag(tag === '__none__' ? '' : '__none__')} className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between ${tag === '__none__' ? 'bg-indigo-500/15 text-white border border-indigo-500/30' : 'text-slate-400 hover:bg-white/5 border border-transparent'}`}>
                  <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-slate-500" /> Sans tag</span>
                  <span className="text-[10px] text-slate-500">{items.filter(v => v.tags.length === 0).length}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="col-span-9">
          {canManage && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 mb-3 flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <Checkbox data-testid="select-all-checkbox" checked={selected.length > 0 && selected.length === filtered.length} onCheckedChange={selectAll} className="border-white/20" />
              Tout sélectionner ({filtered.length})
            </label>
            <span className="text-xs text-slate-600">Astuce : Maj+clic pour sélectionner une plage</span>
            {selected.length > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button data-testid="assign-tag-button" size="sm" variant="outline" className="h-7 text-xs border-indigo-500/30 bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20">
                      <Tag size={11} className="mr-1" /> Assigner un tag ({selected.length})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#0B0B0E] border-white/10 text-slate-200">
                    {allTags.map(t => (
                      <DropdownMenuItem key={t} data-testid={`assign-tag-${t}`} onClick={() => applyTag(t)} className="focus:bg-white/5 text-xs">
                        <span className="w-2 h-2 rounded-full mr-2 shrink-0" style={{ backgroundColor: tagColors[t] || '#64748b' }} /> {t}
                      </DropdownMenuItem>
                    ))}
                    {allTags.length > 0 && <DropdownMenuSeparator className="bg-white/5" />}
                    <DropdownMenuItem data-testid="assign-new-tag" onClick={() => setTagging(true)} className="focus:bg-white/5 text-xs text-indigo-300">
                      <Plus size={11} className="mr-1.5" /> Nouveau tag…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {selectedTags.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button data-testid="remove-tag-button" size="sm" variant="outline" className="h-7 text-xs border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20">
                        <X size={11} className="mr-1" /> Retirer un tag
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#0B0B0E] border-white/10 text-slate-200">
                      {selectedTags.map(([t, n]) => (
                        <DropdownMenuItem key={t} data-testid={`remove-tag-${t}`} onClick={() => removeTag(t)} className="focus:bg-white/5 text-xs">
                          <span className="w-2 h-2 rounded-full mr-2 shrink-0" style={{ backgroundColor: tagColors[t] || '#64748b' }} /> {t}
                          <span className="ml-auto pl-3 text-[10px] text-slate-500">{n}</span>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator className="bg-white/5" />
                      <DropdownMenuItem data-testid="remove-all-tags" onClick={() => removeTag(null)} className="focus:bg-white/5 text-xs text-amber-300">
                        <X size={11} className="mr-1.5" /> Retirer tous les tags
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <Button data-testid="delete-selected-button" size="sm" variant="outline" className="h-7 text-xs border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20" onClick={() => remove(selected)}>
                  <Trash2 size={11} className="mr-1" /> Supprimer {selected.length} sélectionné(s)
                </Button>
              </div>
            )}
          </div>
          )}

          {pageItems.length === 0 ? (
            <EmptyState icon={Folder} title="Aucun média dans ton Vault" desc="Importe ton premier média pour commencer." />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {pageItems.map(v => {
                const Ic = TYPE_ICONS[v.type] || ImageIcon;
                const sel = selected.includes(v.id);
                return (
                  <div key={v.id} className={`rounded-2xl border overflow-hidden transition-colors group ${sel ? 'border-indigo-400' : 'border-white/10 hover:border-indigo-500/30'} bg-white/[0.02]`} data-testid={`vault-item-${v.id}`}>
                    <button type="button" onClick={() => setPreview(v)} className="aspect-square relative bg-[#101013] w-full block cursor-zoom-in" data-testid={`vault-preview-${v.id}`}>
                      <HoverMedia v={v} />
                      <Badge className="absolute top-2 left-2 bg-black/60 text-white border-0 capitalize text-[9px]"><Ic size={8} className="mr-1" /> {v.type}</Badge>
                      {canManage && (
                        <div className="absolute top-2 right-2" onClick={(e) => { e.stopPropagation(); toggleSelect(v.id, e); }}>
                          <Checkbox data-testid={`vault-select-${v.id}`} checked={sel} onCheckedChange={() => {}} className="bg-white border-white data-[state=checked]:bg-indigo-500 pointer-events-none" />
                        </div>
                      )}
                      {canManage && (
                        <div className="absolute right-2 top-9 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                          <button data-testid={`edit-vault-${v.id}`} onClick={() => setEditing(v)} className="w-7 h-7 rounded-full bg-white/90 text-slate-900 flex items-center justify-center hover:bg-white"><Pencil size={11} /></button>
                          <button data-testid={`delete-vault-${v.id}`} onClick={() => remove([v.id])} className="w-7 h-7 rounded-full bg-white/90 text-slate-900 flex items-center justify-center hover:bg-rose-100 hover:text-rose-600"><Trash2 size={11} /></button>
                        </div>
                      )}
                    </button>
                    <div className="p-3">
                      <div className="text-sm font-medium text-white truncate mb-0.5">{v.name}</div>
                      <div className="text-[10px] text-slate-500 capitalize mb-1">{v.type} · {v.size_mb > 0 ? `${v.size_mb} Mo` : `${v.sent} envois`}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {v.tags.length === 0 && <span className="text-[10px] text-slate-600">Sans tag</span>}
                        {v.tags.map(tg => (
                          <span key={tg} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: (tagColors[tg] || '#64748b') + '33', color: tagColors[tg] || '#94a3b8', border: `1px solid ${(tagColors[tg] || '#64748b')}66` }}>{tg}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
            <span>Affichage {filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} sur {filtered.length}</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 rounded bg-white/5 disabled:opacity-30">‹</button>
              <span>Page {page} sur {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-2 py-1 rounded bg-white/5 disabled:opacity-30">›</button>
            </div>
          </div>
        </div>
      </div>

      <UploadDialog open={uploading} account={account} allTags={allTags} onClose={() => setUploading(false)} onCreated={() => { setUploading(false); load(account); }} />
      <EditDialog item={editing} onClose={() => setEditing(null)} onSaved={(updated) => { setItems(items.map(x => x.id === updated.id ? updated : x)); setEditing(null); }} />
      <LightboxDialog preview={preview} onClose={() => setPreview(null)} tagColors={tagColors} />

      <AddTagDialog
        open={tagging}
        count={selected.length}
        allTags={allTags}
        tagColors={tagColors}
        onClose={() => setTagging(false)}
        onApply={applyTag}
        onSelectAll={() => setSelected(filtered.map(v => v.id))}
      />
      <VaultSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        sort={sort}
        setSort={setSort}
        type={type}
        setType={setType}
        cacheStats={cacheStats}
        onClearAllCache={async () => {
          if (!window.confirm('Vider les file_ids Telegram en cache pour TOUS les médias du Vault ? Le prochain envoi les re-uploadera. C\'est sans risque.')) return;
          try {
            await Promise.all(items.map(v => api.delete(`/vault/${v.id}/cache`)));
            toast.success('Cache vidé');
            api.get('/vault/cache-stats').then(({ data }) => setCacheStats(data)).catch(() => {});
          } catch (e) { toast.error(apiError(e, 'Échec du vidage')); }
        }}
      />
    </div>
  );
}
